import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Department {
  id: string;
  name: string;
  description: string | null;
  color: string;
  company_id: string;
  created_at: string;
  updated_at: string;
}

export function useDepartments() {
  const { profile } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelName = useRef(`departments-changes-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!profile?.company_id) {
      setLoading(false);
      return;
    }

    fetchDepartments();

    // Set up real-time listener
    const channel = supabase
      .channel(channelName.current)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'departments',
          filter: `company_id=eq.${profile.company_id}`,
        },
        () => {
          fetchDepartments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.company_id]);

  const fetchDepartments = async () => {
    if (!profile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('name');

      if (fetchError) throw fetchError;

      setDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return {
    departments,
    loading,
    error,
    refetch: fetchDepartments,
  };
}

